import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Paper,
  Chip,
  Avatar,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Divider,
  Button,
  IconButton,
  Stack,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert,
} from '@mui/material';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from 'recharts';
import {
  Analytics as AnalyticsIcon,
  ThumbUp,
  Visibility,
  TrendingUp,
  FilterList,
  Refresh,
  LockOutlined,
} from '@mui/icons-material';
import { analyticsAPI } from '../services/api';
import { SHOW_INSTAGRAM_UI } from '../config/featureFlags';
import { useClients } from '../contexts/ClientContext';
import ClientScopedGate from '../components/ClientScopedGate';
import { useScopedClientSelection } from '../hooks/useScopedClientSelection';
import { useToast } from '../contexts/ToastContext';
import { ClientSelector, MetricCard, PageHeader } from '../components/primitives';

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8'];

/** Phase 16.3: human label for engagement sync freshness. */
function formatSyncedAgo(iso) {
  if (!iso) return 'never synced';
  const t = new Date(iso).getTime();
  if (!Number.isFinite(t)) return 'never synced';
  const sec = Math.max(0, Math.floor((Date.now() - t) / 1000));
  if (sec < 60) return 'synced just now';
  const min = Math.floor(sec / 60);
  if (min < 60) return `synced ${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 48) return `synced ${hr}h ago`;
  const days = Math.floor(hr / 24);
  return `synced ${days}d ago`;
}

function Analytics() {
  const { clients: clientsList, setLastSelectedClientId } = useClients();
  const { clientId, selectClient } = useScopedClientSelection();
  const { showError, showSuccess } = useToast();
  const [timeRange] = useState('7d');
  const [analyticsData, setAnalyticsData] = useState(null);
  const [analyticsError, setAnalyticsError] = useState('');
  const [loading, setLoading] = useState(true);
  const [chartData, setChartData] = useState([]);
  const [pieChartData, setPieChartData] = useState([]);
  const [topPosts, setTopPosts] = useState([]);
  const [captionPerf, setCaptionPerf] = useState(null);
  const [captionPerfLoading, setCaptionPerfLoading] = useState(false);
  const [perfClientId, setPerfClientId] = useState('');
  const [perfTimeRange, setPerfTimeRange] = useState('90d');
  const [gscCorrelation, setGscCorrelation] = useState(null);
  const [gscCorrelationLoading, setGscCorrelationLoading] = useState(false);
  const [gscCorrelationClientId, setGscCorrelationClientId] = useState('');
  const [backfillBusy, setBackfillBusy] = useState(false);

  const displayPieChartData = useMemo(() => {
    if (SHOW_INSTAGRAM_UI) return pieChartData;
    return pieChartData.filter(
      (d) => String(d.name || '').toLowerCase() !== 'instagram',
    );
  }, [pieChartData]);

  const displayTopPosts = useMemo(() => {
    if (SHOW_INSTAGRAM_UI) return topPosts;
    return topPosts.filter(
      (p) => String(p.platform || '').toLowerCase() !== 'instagram',
    );
  }, [topPosts]);

  // Calculate metrics
  const totalPosts = analyticsData?.total_posts || 0;
  const totalEngagement = analyticsData?.total_engagement || 0;
  const totalReach =
    analyticsData?.total_reach ?? analyticsData?.total_reach_estimated ?? 0;
  const avgEngagementRateApprox =
    analyticsData?.avg_engagement_rate_approx ??
    analyticsData?.avg_engagement_rate ??
    0;
  const hasAnalyticsData =
    totalPosts > 0 ||
    chartData.length > 0 ||
    displayPieChartData.length > 0 ||
    displayTopPosts.length > 0;

  useEffect(() => {
    fetchAnalyticsData();
  }, [timeRange, clientId]);

  useEffect(() => {
    if (!clientId) return;
    const idStr = String(clientId);
    setPerfClientId((prev) => prev || idStr);
    setGscCorrelationClientId((prev) => prev || idStr);
  }, [clientId]);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      setCaptionPerfLoading(true);
      try {
        const cid = perfClientId ? parseInt(perfClientId, 10) : null;
        const data = await analyticsAPI.getCaptionTemplatePerformance({
          clientId: Number.isFinite(cid) && cid > 0 ? cid : null,
          timeRange: perfTimeRange,
        });
        if (!cancelled) setCaptionPerf(data);
      } catch (e) {
        if (!cancelled) {
          setCaptionPerf({ groups: [], posts_matched: 0 });
          if (perfClientId) {
            showError(
              e?.response?.data?.detail ||
                e?.message ||
                'Failed to load caption performance',
            );
          }
        }
      } finally {
        if (!cancelled) setCaptionPerfLoading(false);
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [perfClientId, perfTimeRange, showError]);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      const cid = gscCorrelationClientId
        ? parseInt(gscCorrelationClientId, 10)
        : null;
      if (!Number.isFinite(cid) || cid <= 0) {
        setGscCorrelation(null);
        return;
      }

      setGscCorrelationLoading(true);
      try {
        const data = await analyticsAPI.getGscEngagementCorrelation({
          clientId: cid,
          limit: 10,
        });
        if (!cancelled) setGscCorrelation(data);
      } catch (e) {
        if (!cancelled) {
          setGscCorrelation({
            connected: false,
            reason: 'load_failed',
            rows: [],
          });
          showError(
            e?.response?.data?.detail ||
              e?.message ||
              'Failed to load GSC correlation data',
          );
        }
      } finally {
        if (!cancelled) setGscCorrelationLoading(false);
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [gscCorrelationClientId, showError]);

  const fetchAnalyticsData = async () => {
    try {
      setLoading(true);
      setAnalyticsError('');
      const cid = clientId ? parseInt(clientId, 10) : null;
      const scoped =
        Number.isFinite(cid) && cid > 0 ? cid : null;
      const response = await analyticsAPI.getAnalytics(timeRange, scoped);
      const data = response.data || {};
      setAnalyticsData(data);

      setChartData(
        Array.isArray(data.engagement_over_time)
          ? data.engagement_over_time
          : [],
      );
      setPieChartData(
        Array.isArray(data.platform_distribution)
          ? data.platform_distribution
          : [],
      );
      setTopPosts(Array.isArray(data.top_posts) ? data.top_posts : []);
    } catch (error) {
      console.error('Error fetching analytics:', error);
      setAnalyticsError(
        'Analytics data is unavailable. Connect an account and publish posts, then refresh once sync has run.',
      );
      setAnalyticsData(null);
      setChartData([]);
      setPieChartData([]);
      setTopPosts([]);
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = () => {
    fetchAnalyticsData();
  };

  const handleBackfillEngagement = async () => {
    const cid = clientId ? parseInt(clientId, 10) : null;
    if (!Number.isFinite(cid) || cid <= 0) {
      showError('Select a client to backfill engagement.');
      return;
    }
    setBackfillBusy(true);
    try {
      const res = await analyticsAPI.backfillEngagement(cid);
      const n =
        Number(res?.queued_posts || 0) + Number(res?.queued_client_posts || 0);
      showSuccess(
        n > 0
          ? `Queued engagement sync for ${n} post(s). Metrics update as workers finish.`
          : 'No published posts needed a sync queue.',
      );
      fetchAnalyticsData();
    } catch (e) {
      showError(
        e?.response?.data?.detail ||
          e?.message ||
          'Failed to queue engagement backfill',
      );
    } finally {
      setBackfillBusy(false);
    }
  };

  return (
    <Box sx={{ p: 4 }}>
      <PageHeader
        title="Analytics Dashboard"
        gradientTitle
        actions={
          <>
            <ClientSelector
              clients={clientsList}
              value={clientId}
              onChange={(client) => client && selectClient(client)}
            />
            <Button
              variant="outlined"
              startIcon={<FilterList />}
              sx={{
                border: '1px solid rgba(88, 166, 255, 0.3)',
                color: '#58a6ff',
                backgroundColor: 'rgba(88, 166, 255, 0.05)',
                '&:hover': {
                  borderColor: '#58a6ff',
                  backgroundColor: 'rgba(88, 166, 255, 0.1)',
                  boxShadow: '0 4px 12px rgba(88, 166, 255, 0.2)',
                },
                borderRadius: 2,
                px: 3,
                py: 1,
                fontWeight: 600,
              }}
            >
              Filter List
            </Button>
            <IconButton
              onClick={handleRefresh}
              sx={{
                backgroundColor: 'rgba(88, 166, 255, 0.1)',
                border: '1px solid rgba(88, 166, 255, 0.3)',
                color: '#58a6ff',
                '&:hover': {
                  backgroundColor: 'rgba(88, 166, 255, 0.2)',
                  transform: 'rotate(180deg)',
                },
                transition: 'all 0.3s ease',
              }}
            >
              <Refresh />
            </IconButton>
          </>
        }
      />

      <ClientScopedGate
        clientId={clientId}
        minHeight={400}
        requirePosts={false}
        acceptGsc
      >
      {analyticsError && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          {analyticsError}
        </Alert>
      )}

      {!loading && !analyticsError && !hasAnalyticsData && (
        <Alert severity="info" sx={{ mb: 3 }}>
          No analytics data yet. Connect a platform account, publish some posts,
          and refresh after the analytics sync runs.
        </Alert>
      )}

      {/* Enhanced Key Metrics */}
      <Grid container spacing={4} sx={{ mb: 6, alignItems: 'stretch' }}>
        <Grid item xs={12} sm={6} md={3} sx={{ display: 'flex' }}>
          <MetricCard
            title="Total Posts"
            value={totalPosts.toLocaleString()}
            icon={<AnalyticsIcon />}
            accentColor="#58a6ff"
            loading={loading}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3} sx={{ display: 'flex' }}>
          <MetricCard
            title="Total Engagement"
            value={totalEngagement.toLocaleString()}
            icon={<ThumbUp />}
            accentColor="#238636"
            loading={loading}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3} sx={{ display: 'flex' }}>
          <MetricCard
            title="Reach / Views"
            value={totalReach.toLocaleString()}
            icon={<Visibility />}
            accentColor="#f78166"
            loading={loading}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3} sx={{ display: 'flex' }}>
          <MetricCard
            title="Approx. avg. engagement rate"
            value={`${avgEngagementRateApprox}%`}
            icon={<TrendingUp />}
            accentColor="#d29922"
            loading={loading}
          />
        </Grid>
      </Grid>

      <Card
        sx={{
          mb: 6,
          background: 'linear-gradient(135deg, #161b22 0%, #21262d 100%)',
          border: '1px solid rgba(48, 54, 61, 0.5)',
          borderRadius: 4,
        }}
      >
        <CardContent sx={{ p: 4 }}>
          <Typography
            variant="h6"
            sx={{ color: '#f0f6fc', fontWeight: 700, mb: 1 }}
          >
            Caption &amp; prompt performance
          </Typography>
          <Typography variant="body2" sx={{ color: '#8b949e', mb: 2 }}>
            Library-promoted posts only: groups by content pillar, whether the
            caption prompt came from your client DB override or the YAML default,
            and the approved variant style. Engagement totals update after the
            delayed platform sync (default ~36h post-publish).
          </Typography>
          <Stack direction="row" flexWrap="wrap" gap={2} sx={{ mb: 2 }}>
            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel id="perf-client" sx={{ color: '#8b949e' }}>
                Client
              </InputLabel>
              <Select
                labelId="perf-client"
                label="Client"
                value={perfClientId}
                onChange={(e) => {
                  const value = e.target.value;
                  setPerfClientId(value);
                  if (value) setLastSelectedClientId(value);
                }}
                sx={{ color: '#f0f6fc' }}
              >
                <MenuItem value="">
                  <em>All clients</em>
                </MenuItem>
                {clientsList.map((c) => (
                  <MenuItem key={c.id} value={String(c.id)}>
                    {c.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel id="perf-range" sx={{ color: '#8b949e' }}>
                Window
              </InputLabel>
              <Select
                labelId="perf-range"
                label="Window"
                value={perfTimeRange}
                onChange={(e) => setPerfTimeRange(e.target.value)}
                sx={{ color: '#f0f6fc' }}
              >
                <MenuItem value="7d">7 days</MenuItem>
                <MenuItem value="30d">30 days</MenuItem>
                <MenuItem value="90d">90 days</MenuItem>
                <MenuItem value="all">All time</MenuItem>
              </Select>
            </FormControl>
          </Stack>
          <Typography variant="caption" sx={{ color: '#6e7681', display: 'block', mb: 1 }}>
            {captionPerfLoading
              ? 'Loading…'
              : `${captionPerf?.posts_matched ?? 0} published posts matched in this window`}
          </Typography>
          <TableContainer
            component={Paper}
            sx={{ bgcolor: '#0d1117', border: '1px solid #30363d' }}
          >
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ color: '#8b949e' }}>Client</TableCell>
                  <TableCell sx={{ color: '#8b949e' }}>Pillar</TableCell>
                  <TableCell sx={{ color: '#8b949e' }}>Prompt source</TableCell>
                  <TableCell sx={{ color: '#8b949e' }}>Template</TableCell>
                  <TableCell sx={{ color: '#8b949e' }}>Variant</TableCell>
                  <TableCell align="right" sx={{ color: '#8b949e' }}>
                    Posts
                  </TableCell>
                  <TableCell align="right" sx={{ color: '#8b949e' }}>
                    Avg eng.
                  </TableCell>
                  <TableCell align="right" sx={{ color: '#8b949e' }}>
                    Total eng.
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(captionPerf?.groups || []).length === 0 && !captionPerfLoading ? (
                  <TableRow>
                    <TableCell colSpan={8} sx={{ color: '#8b949e' }}>
                      No attributed published posts yet. Promote from the Gallery,
                      publish with a linked platform account, and wait for the
                      engagement sync task.
                    </TableCell>
                  </TableRow>
                ) : (
                  (captionPerf?.groups || []).map((g, i) => (
                    <TableRow key={`${g.client_id}-${g.content_pillar}-${g.variant_style}-${i}`}>
                      <TableCell sx={{ color: '#c9d1d9' }}>{g.client_name}</TableCell>
                      <TableCell sx={{ color: '#c9d1d9' }}>{g.content_pillar}</TableCell>
                      <TableCell sx={{ color: '#c9d1d9' }}>{g.prompt_source}</TableCell>
                      <TableCell sx={{ color: '#8b949e' }}>{g.prompt_template_key}</TableCell>
                      <TableCell sx={{ color: '#58a6ff' }}>{g.variant_style}</TableCell>
                      <TableCell align="right" sx={{ color: '#c9d1d9' }}>
                        {g.published_posts}
                      </TableCell>
                      <TableCell align="right" sx={{ color: '#3fb950' }}>
                        {g.avg_engagement_per_measured_post}
                      </TableCell>
                      <TableCell align="right" sx={{ color: '#c9d1d9' }}>
                        {g.total_engagement}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      <Card
        sx={{
          mb: 6,
          background: 'linear-gradient(135deg, #161b22 0%, #21262d 100%)',
          border: '1px solid rgba(48, 54, 61, 0.5)',
          borderRadius: 4,
        }}
      >
        <CardContent sx={{ p: 4 }}>
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={2}
            alignItems={{ xs: 'stretch', md: 'center' }}
            justifyContent="space-between"
            sx={{ mb: 2 }}
          >
            <Box>
              <Typography variant="h6" sx={{ color: '#f0f6fc', fontWeight: 700 }}>
                GSC + Social Engagement Correlation
              </Typography>
              <Typography variant="body2" sx={{ color: '#8b949e', mt: 0.5 }}>
                Top Search Console topics matched to published posts in the same GSC window.
              </Typography>
            </Box>
            <FormControl size="small" sx={{ minWidth: 260 }}>
              <InputLabel sx={{ color: '#8b949e' }}>Client</InputLabel>
              <Select
                value={gscCorrelationClientId}
                label="Client"
                onChange={(e) => {
                  const value = e.target.value;
                  setGscCorrelationClientId(value);
                  if (value) setLastSelectedClientId(value);
                }}
                sx={{
                  color: '#f0f6fc',
                  '.MuiOutlinedInput-notchedOutline': {
                    borderColor: 'rgba(48, 54, 61, 0.8)',
                  },
                }}
              >
                <MenuItem value="">Choose a client</MenuItem>
                {(clientsList || []).map((client) => (
                  <MenuItem key={client.id} value={String(client.id)}>
                    {client.name || client.company || `Client ${client.id}`}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>

          {!gscCorrelationClientId ? (
            <Alert severity="info">
              Select a client with Google Search Console connected and synced social engagement.
            </Alert>
          ) : gscCorrelationLoading ? (
            <Typography sx={{ color: '#8b949e' }}>Loading correlation data…</Typography>
          ) : gscCorrelation?.reason === 'gsc_not_connected' ? (
            <Alert severity="info">
              Google Search Console is not connected for this client.
            </Alert>
          ) : gscCorrelation?.reason === 'no_gsc_snapshots' ? (
            <Alert severity="info">
              Search Console is connected, but no GSC snapshots have been synced yet.
            </Alert>
          ) : !gscCorrelation || (gscCorrelation.rows || []).length === 0 ? (
            <Alert severity="info">
              No combined GSC and engagement rows yet for this client.
            </Alert>
          ) : (
            <>
              <Typography variant="caption" sx={{ color: '#8b949e', display: 'block', mb: 2 }}>
                Window: {gscCorrelation.period_start} to {gscCorrelation.period_end}
                {' '}· {gscCorrelation.matched_rows || 0} of {gscCorrelation.topics_checked || 0}
                {' '}topics matched published posts.
              </Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ color: '#8b949e' }}>Topic</TableCell>
                      <TableCell align="right" sx={{ color: '#8b949e' }}>GSC Clicks</TableCell>
                      <TableCell align="right" sx={{ color: '#8b949e' }}>Impressions</TableCell>
                      <TableCell align="right" sx={{ color: '#8b949e' }}>Posts</TableCell>
                      <TableCell align="right" sx={{ color: '#8b949e' }}>Social Engagement</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(gscCorrelation.rows || []).map((row) => (
                      <TableRow key={row.topic}>
                        <TableCell sx={{ color: '#f0f6fc' }}>{row.topic}</TableCell>
                        <TableCell align="right" sx={{ color: '#3fb950' }}>
                          {Number(row.gsc_clicks || 0).toLocaleString()}
                        </TableCell>
                        <TableCell align="right" sx={{ color: '#c9d1d9' }}>
                          {Number(row.gsc_impressions || 0).toLocaleString()}
                        </TableCell>
                        <TableCell align="right" sx={{ color: '#c9d1d9' }}>
                          {Number(row.matched_posts || 0).toLocaleString()}
                        </TableCell>
                        <TableCell align="right" sx={{ color: '#58a6ff' }}>
                          {Number(row.social_engagement || 0).toLocaleString()}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}
        </CardContent>
      </Card>

      {/* Charts Section */}
      <Box sx={{ display: 'flex', gap: 4, mb: 6 }}>
        {/* Engagement Over Time */}
        <Box sx={{ flex: '0 0 65%' }}>
          <Card
            sx={{
              background: 'linear-gradient(135deg, #161b22 0%, #21262d 100%)',
              border: '1px solid rgba(48, 54, 61, 0.5)',
              borderRadius: 4,
              boxShadow:
                '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              '&:hover': {
                borderColor: '#58a6ff',
                transform: 'translateY(-2px)',
                boxShadow:
                  '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
              },
            }}
          >
            <CardContent sx={{ p: 4 }}>
              <Typography
                variant="h6"
                sx={{
                  mb: 3,
                  color: '#f0f6fc',
                  fontWeight: 700,
                  fontSize: '18px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                }}
              >
                <Box
                  sx={{
                    background: 'linear-gradient(135deg, #8884d8, #82ca9d)',
                    borderRadius: 3,
                    p: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: '0 4px 12px rgba(136, 132, 216, 0.3)',
                  }}
                >
                  <BarChart sx={{ color: 'white', fontSize: 18 }} />
                </Box>
                Engagement Over Time
              </Typography>
              {chartData && chartData.length > 0 ? (
                <ResponsiveContainer width="100%" height={450}>
                  <BarChart data={chartData}>
                    <CartesianGrid
                      strokeDasharray="3 3"
                      stroke="rgba(255, 255, 255, 0.1)"
                    />
                    <XAxis
                      dataKey="date"
                      tick={{ fill: '#f0f6fc' }}
                      axisLine={{ stroke: 'rgba(255, 255, 255, 0.2)' }}
                    />
                    <YAxis
                      tick={{ fill: '#f0f6fc' }}
                      axisLine={{ stroke: 'rgba(255, 255, 255, 0.2)' }}
                    />
                    <RechartsTooltip
                      contentStyle={{
                        backgroundColor: 'rgba(13, 17, 23, 0.95)',
                        border: '1px solid rgba(48, 54, 61, 0.6)',
                        borderRadius: 8,
                        color: '#f0f6fc',
                      }}
                    />
                    <Legend wrapperStyle={{ color: '#f0f6fc' }} />
                    <Bar dataKey="engagement" fill="#8884d8" />
                    <Bar dataKey="likes" fill="#82ca9d" />
                    <Bar dataKey="comments" fill="#ffc658" />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <Box
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                  height={450}
                  sx={{ color: '#8b949e' }}
                >
                  <Typography variant="body1">
                    No engagement data available for the selected time period
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Box>

        {/* Platform Distribution */}
        <Box sx={{ flex: '0 0 35%' }}>
          <Card
            sx={{
              background: 'linear-gradient(135deg, #161b22 0%, #21262d 100%)',
              border: '1px solid rgba(48, 54, 61, 0.5)',
              borderRadius: 4,
              boxShadow:
                '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              '&:hover': {
                borderColor: '#58a6ff',
                transform: 'translateY(-2px)',
                boxShadow:
                  '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
              },
            }}
          >
            <CardContent sx={{ p: 4 }}>
              <Typography
                variant="h6"
                sx={{
                  mb: 3,
                  color: '#f0f6fc',
                  fontWeight: 700,
                  fontSize: '18px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                }}
              >
                <Box
                  sx={{
                    background: 'linear-gradient(135deg, #ffc658, #ff8042)',
                    borderRadius: 3,
                    p: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: '0 4px 12px rgba(255, 198, 88, 0.3)',
                  }}
                >
                  <BarChart sx={{ color: 'white', fontSize: 18 }} />
                </Box>
                Platform Distribution
              </Typography>
              <Typography variant="body2" sx={{ color: '#8b949e', mb: 2 }}>
                Published posts by stored platform in the selected window.
                Engagement shows a dash when metrics are unavailable (LinkedIn
                API limitation / publish-only, or YouTube quota skip), not a misleading zero.
              </Typography>
              {displayPieChartData.length > 0 ? (
                <>
                <ResponsiveContainer width="100%" height={450}>
                  <PieChart>
                    <Pie
                      data={displayPieChartData}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ name, percent }) =>
                        `${name} ${(percent * 100).toFixed(0)}%`
                      }
                      outerRadius={140}
                      fill="#8884d8"
                      dataKey="value"
                    >
                      {displayPieChartData.map((entry, index) => (
                        <Cell
                          key={`cell-${index}`}
                          fill={COLORS[index % COLORS.length]}
                        />
                      ))}
                    </Pie>
                    <RechartsTooltip
                      formatter={(value, _name, item) => {
                        const payload = item?.payload || {};
                        const eng =
                          payload.engagement_status === 'unavailable' ||
                          payload.engagement == null
                            ? '—'
                            : Number(payload.engagement || 0).toLocaleString();
                        return [
                          `${Number(value || 0).toLocaleString()} published posts · engagement ${eng}`,
                          payload.name || 'Platform',
                        ];
                      }}
                      contentStyle={{
                        backgroundColor: 'rgba(13, 17, 23, 0.95)',
                        border: '1px solid rgba(48, 54, 61, 0.6)',
                        borderRadius: 8,
                        color: '#f0f6fc',
                      }}
                    />
                  </PieChart>
                </ResponsiveContainer>
                <List dense sx={{ mt: 1 }}>
                  {displayPieChartData.map((row) => {
                    const unavailable =
                      row.engagement_status === 'unavailable' ||
                      (row.engagement == null &&
                        Number(row.unavailable_posts || 0) > 0);
                    const engLabel = unavailable
                      ? row.unavailable_message ||
                        'Metric unavailable'
                      : row.engagement_status === 'partial'
                        ? `${Number(row.engagement || 0).toLocaleString()} (some unavailable)`
                        : Number(row.engagement || 0).toLocaleString();
                    return (
                      <ListItem
                        key={row.name || row.platform}
                        sx={{ px: 0, color: '#c9d1d9' }}
                      >
                        <ListItemAvatar sx={{ minWidth: 36 }}>
                          {unavailable ? (
                            <LockOutlined sx={{ color: '#8b949e', fontSize: 20 }} />
                          ) : (
                            <TrendingUp sx={{ color: '#8b949e', fontSize: 20 }} />
                          )}
                        </ListItemAvatar>
                        <ListItemText
                          primary={row.name || row.platform}
                          secondary={
                            unavailable
                              ? engLabel
                              : `${Number(row.value || 0).toLocaleString()} posts · engagement ${engLabel}`
                          }
                          primaryTypographyProps={{ color: '#f0f6fc' }}
                          secondaryTypographyProps={{ color: '#8b949e' }}
                        />
                        <Typography
                          variant="body2"
                          sx={{ color: unavailable ? '#8b949e' : '#f0f6fc', minWidth: 48, textAlign: 'right' }}
                        >
                          {unavailable ? '—' : Number(row.engagement || 0).toLocaleString()}
                        </Typography>
                      </ListItem>
                    );
                  })}
                </List>
                </>
              ) : (
                <Box
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                  height={450}
                  sx={{ color: '#8b949e', textAlign: 'center' }}
                >
                  <Typography variant="body1">
                    No platform distribution data yet.
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Box>
      </Box>

      {/* Top Performing Posts */}
      <Card>
        <CardContent>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={1}
            alignItems={{ sm: 'center' }}
            justifyContent="space-between"
            sx={{ mb: 1 }}
          >
            <Typography variant="h6" gutterBottom={false}>
              Top Performing Posts
            </Typography>
            <Button
              size="small"
              variant="outlined"
              startIcon={<Refresh />}
              disabled={!clientId || backfillBusy}
              onClick={handleBackfillEngagement}
              sx={{
                whiteSpace: 'nowrap',
                borderColor: 'rgba(88, 166, 255, 0.4)',
                color: '#58a6ff',
              }}
            >
              {backfillBusy ? 'Queueing…' : 'Backfill engagement'}
            </Button>
          </Stack>
          <Typography variant="body2" sx={{ color: '#8b949e', mb: 1.5 }}>
            Shows when metrics were last synced. Backfill re-queues this client&apos;s
            full published history (Celery).
          </Typography>
          <List>
            {displayTopPosts.length === 0 && (
              <ListItem>
                <ListItemText
                  primary="No top posts yet"
                  secondary="Publish posts and wait for engagement sync to populate this list."
                />
              </ListItem>
            )}
            {displayTopPosts.map((post, index) => {
              const engagement =
                post.engagement ??
                (post.engagement_metrics?.likes || 0) +
                  (post.engagement_metrics?.comments || 0) +
                  (post.engagement_metrics?.shares || 0);
              const syncedLabel = formatSyncedAgo(post.last_synced_at);
              const stale = Boolean(post.sync_stale);
              return (
                <React.Fragment key={`${post.record_kind || 'post'}-${post.id}`}>
                  <ListItem>
                    <ListItemAvatar>
                      <Avatar sx={{ bgcolor: COLORS[index % COLORS.length] }}>
                        {index + 1}
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={post.content || post.title || `Post #${post.id}`}
                      secondary={`${post.platform} • ${engagement} total engagement • ${syncedLabel}${
                        stale ? ' (stale)' : ''
                      }`}
                    />
                    <Stack direction="row" spacing={0.75} alignItems="center">
                      <Chip
                        label={syncedLabel}
                        size="small"
                        variant="outlined"
                        color={stale || !post.last_synced_at ? 'warning' : 'default'}
                        sx={{ color: '#c9d1d9', borderColor: '#30363d' }}
                      />
                      <Chip
                        label={`${engagement} engagement`}
                        color="primary"
                        size="small"
                      />
                    </Stack>
                  </ListItem>
                  {index < displayTopPosts.length - 1 && <Divider />}
                </React.Fragment>
              );
            })}
          </List>
        </CardContent>
      </Card>
      </ClientScopedGate>
    </Box>
  );
}

export default Analytics;
